import { PlaybookApiError } from "@playbookai/api-clients";

/**
 * What to tell someone whose Connect was refused (PB-4734).
 *
 * SHELL-OWNED and a pure function, for the same two reasons as
 * [[offersReauthorize]] next door: which refusals deserve their own wording is a
 * PRODUCT decision rather than a fact about the wire contract, and a policy is
 * worth pinning independently of how a row happens to be laid out.
 *
 * The gap this closes: `startAuthorization` had no error rendering anywhere, so
 * `connect.mutate()` either navigated away to the provider or did nothing at
 * all. Every refusal was therefore indistinguishable from a dead button — and
 * that is worst for this particular call, because its most common refusal is
 * not a fault at all. Access is default-deny (PB-4542), so an integration an
 * administrator has switched ON is still unusable by everyone they have not
 * granted, and there was no way to learn that from the console.
 *
 * `INTEGRATION_NOT_GRANTED` is the only code named, because it is the only one
 * whose remedy is different: ask someone, rather than try again. Everything else
 * gets the generic retry line — this surface will not paraphrase codes it was
 * not written to explain, since a confidently wrong explanation is worse than an
 * honest vague one.
 */
export function connectRefusalMessage(error: unknown): string {
  const code =
    error instanceof PlaybookApiError ? error.envelope.code : undefined;
  return code === "INTEGRATION_NOT_GRANTED"
    ? "You do not have access to this integration yet. Ask an admin to grant you access, then try again."
    : "Could not start the connection. Please try again.";
}
